import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/api';
import { ArrowLeft, Package, CreditCard, MapPin, Calendar, QrCode, Radio } from 'lucide-react';
import UserLayout from '../../components/user/UserLayout';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function UserOrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/orders/${orderId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order details');
      }

      setOrder(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'dispatched':
        return 'bg-purple-100 text-purple-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading order details...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  if (error || !order) {
    return (
      <UserLayout>
        <div className="p-8">
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error || 'Order not found'}
          </div>
          <Button
            onClick={() => navigate('/user/orders')}
            className="mt-4 bg-navy text-white hover:bg-navy/90"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </Button>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/user/orders')}
              className="bg-gray-100 text-navy hover:bg-gray-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-extrabold text-navy">Order #{order.transaction_id?.slice(0, 12)}</h1>
              <p className="text-sm text-gray-500 mt-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {formatDate(order.created_at)}
              </p>
            </div>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(order.order_status)}`}>
            {(order.order_status || 'ordered').charAt(0).toUpperCase() + (order.order_status || 'ordered').slice(1)}
          </span>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Information */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-navy" />
                <h2 className="text-xl font-bold text-navy">Order Information</h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Product:</span>
                  <span className="font-semibold text-navy">{order.order_data?.cardType || 'N/A'}</span>
                </div>
                {order.order_data?.selectedMaterial && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Material:</span>
                    <span className="font-semibold text-navy">{order.order_data.selectedMaterial.name}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Price:</span>
                  <span className="font-semibold text-navy">${order.order_data?.selectedMaterial?.price || order.amount}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Quantity:</span>
                  <span className="font-semibold text-navy">{order.order_data?.quantity || 1}</span>
                </div>
                {order.coupon && (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold text-navy" data-testid="user-order-subtotal">
                        ${Number(order.subtotal ?? order.amount).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600">
                        Coupon: <span className="font-mono text-navy">{order.coupon.code}</span>
                        {order.coupon.discount_type === 'percentage'
                          ? ` (${order.coupon.discount_value}% off)`
                          : ` ($${order.coupon.discount_value} off)`}
                      </span>
                      <span className="font-semibold text-green-700" data-testid="user-order-discount">
                        -${Number(order.coupon.discount_amount || 0).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 text-lg">Total Amount:</span>
                  <span className="font-bold text-navy text-xl">${order.amount}</span>
                </div>
              </div>
            </div>

            {/* Card Details */}
            {order.order_data?.displayName && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-navy mb-4">Card Details</h2>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Business Name:</span>
                    <span className="font-semibold text-navy">{order.order_data.displayName}</span>
                  </div>
                  {order.order_data.address && (
                    <div className="py-2">
                      <p className="text-gray-600 text-sm mb-1">Address on Card:</p>
                      <p className="font-semibold">{order.order_data.address}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tracking Statistics (if delivered) */}
            {order.order_status === 'delivered' && order.dynamic_link_details && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-navy mb-4">Card Statistics</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <QrCode className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-semibold text-gray-700">QR Scans</span>
                    </div>
                    <p className="text-3xl font-bold text-green-600">{order.dynamic_link_details.qr_scans || 0}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Radio className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-semibold text-gray-700">NFC Taps</span>
                    </div>
                    <p className="text-3xl font-bold text-purple-600">{order.dynamic_link_details.nfc_taps || 0}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Shipping Info */}
          <div className="space-y-6">
            {/* Shipping Address */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-navy" />
                <h2 className="text-xl font-bold text-navy">Shipping Address</h2>
              </div>
              {order.shipping_address ? (
                <div className="text-gray-700 space-y-1">
                  <p className="font-semibold text-navy">
                    {order.shipping_address.firstName} {order.shipping_address.lastName}
                  </p>
                  {order.shipping_address.email && (
                    <p className="text-sm text-gray-600">{order.shipping_address.email}</p>
                  )}
                  {order.shipping_address.phone && (
                    <p className="text-sm text-gray-600">{order.shipping_address.phone}</p>
                  )}
                  <p className="pt-2">{order.shipping_address.addressLine1}</p>
                  {order.shipping_address.addressLine2 && (
                    <p>{order.shipping_address.addressLine2}</p>
                  )}
                  <p>
                    {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zipCode}
                  </p>
                  <p>{order.shipping_address.country}</p>
                </div>
              ) : (
                <p className="text-gray-500">No shipping address available</p>
              )}
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-5 h-5 text-navy" />
                <h2 className="text-xl font-bold text-navy">Payment</h2>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Status:</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {order.payment_status || 'pending'}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-bold text-navy">${order.amount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </UserLayout>
  );
}
